import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuit, Lightbulb, Layout, Globe, Lock, ArrowRight, ChevronLeft, Sparkles, Camera, Image as ImageIcon, Shield, Key, Calendar, Radio, Users, Search, Loader, Info, X } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useAuth } from '../context/AuthContext';
import { useCreation } from '../hooks/useCreation';
import { themeClasses } from '../utils/themes';
import Breadcrumbs from '../components/Breadcrumbs';

const BrainstormCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const community_id = searchParams.get('community_id');
  const { addRoom } = useActivities();

  const theme = themeClasses['brainstorm'];

  const {
    step,
    setStep,
    loading,
    setLoading,
    error,
    setError,
    image: boardImage,
    setImage: setBoardImage,
    fileInputRef,
    speakers,
    speakerInput,
    setSpeakerInput,
    addSpeaker,
    removeSpeaker,
    searchMembers,
    searchResults,
    searching,
    nextStep: triggerNextStep,
    prevStep,
  } = useCreation();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Board cover image size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setBoardImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const TITLE_LIMIT = 100;
  const DESCRIPTION_LIMIT = 500;

  const speakerSearchAllowed = Boolean(community_id || user?.role === 'platform_admin');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (speakerInput.trim().length >= 2 && speakerSearchAllowed) {
        searchMembers(speakerInput, community_id, user?.role === 'platform_admin');
      } else {
        searchMembers('', community_id, speakerSearchAllowed);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [speakerInput, community_id, searchMembers, speakerSearchAllowed, user?.role]);

  const [template, setTemplate] = useState('blank');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timeframe, setTimeframe] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('1h');
  const [period] = useState('always');
  const [anonymous, setAnonymous] = useState(true);
  const [voting, setVoting] = useState(true);
  const [privacy, setPrivacy] = useState('public');
  const [password, setPassword] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);

  const handleCreate = async () => {
    const validate = () => {
      if (!title.trim()) return 'Please enter a board topic';
      if (title.length > TITLE_LIMIT) return `Topic is too long (max ${TITLE_LIMIT} chars)`;
      if (description.length > DESCRIPTION_LIMIT) return `Description is too long (max ${DESCRIPTION_LIMIT} chars)`;
      if (timeframe === 'scheduled' && !scheduledAt) return 'Please select a scheduled date and time';
      if (privacy === 'private' && !password.trim()) return 'Please set a password for your private board';
      return null;
    };

    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      if (!title.trim()) setStep(1);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const newRoom = await addRoom({
        title,
        description,
        type: 'Brainstorm',
        template,
        period,
        timeframe,
        scheduled_at: timeframe === 'scheduled' ? scheduledAt : null,
        duration,
        anonymous, 
        voting,    
        privacy,
        settings: {
          password: privacy === 'private' ? password : null,
          invite_only: privacy === 'private' ? inviteOnly : false,
          speakers: speakers.length > 0 ? speakers.map(s => s.name) : null
        },
        image: boardImage,
        community_id,
        participants: 0,
        host: user?.name || 'You'
      });
      navigate(getRoomPath(newRoom));
    } catch (err) {
      console.error('Room creation failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create board. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    triggerNextStep(() => {
      if (!title.trim()) return 'Please enter a board topic';
      if (title.length > TITLE_LIMIT) return `Topic is too long (max ${TITLE_LIMIT} chars)`;
      if (description.length > DESCRIPTION_LIMIT) return `Description is too long (max ${DESCRIPTION_LIMIT} chars)`;
      return null;
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => step > 1 ? prevStep() : navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <div className="mb-1">
            <Breadcrumbs 
              items={[
                { label: 'Communities', path: '/communities' },
                { label: 'Community', path: `/c/${community_id}` },
                { label: 'New Board' }
              ]} 
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Setup Ideation Board</h1>
          <p className="text-gray-500 font-medium text-sm">Step {step} of 2: {step === 1 ? 'Board Structure' : 'Collaboration Settings'}</p>
        </div>
      </div>

      {step === 1 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <div className="bg-white rounded-3xl border border-gray-100 p-6 md:p-8 space-y-6 shadow-xl shadow-gray-100/50">
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                <Shield size={16} />
                {error}
              </div>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Board Background / Cover</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="relative h-48 rounded-3xl bg-gray-50 border-2 border-dashed border-gray-100 flex flex-col items-center justify-center cursor-pointer group hover:bg-gray-100 transition-all overflow-hidden"
              >
                {boardImage ? (
                  <>
                    <img src={boardImage} alt="Board cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera size={32} className="text-white" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-full bg-white shadow-sm mb-2 text-gray-300 group-hover:text-green-500 transition-colors">
                      <ImageIcon size={32} />
                    </div>
                    <p className="text-xs font-bold text-gray-400">Add a cover image to your board</p>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Board Topic</label>
                <span className={cn("text-xxs font-bold", title.length > TITLE_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {title.length}/{TITLE_LIMIT}
                </span>
              </div>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What are we solving today?"
                className={cn(
                  "w-full px-5 py-4 bg-green-50/30 border-2 border-green-50 rounded-2xl focus:ring-2 focus:ring-green-500/20 focus:border-green-200 font-bold transition-all text-xl placeholder:text-gray-300 outline-none",
                  title.length > TITLE_LIMIT && "border-red-500/50"
                )}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Topic / Description</label>
                <span className={cn("text-xxs font-bold", description.length > DESCRIPTION_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {description.length}/{DESCRIPTION_LIMIT}
                </span>
              </div>
              <textarea 
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What will we interact about?"
                className={cn(
                  "w-full px-5 py-3.5 bg-gray-50 border-none rounded-2xl font-medium transition-all resize-none text-sm focus:ring-2 focus:ring-green-500/10",
                  description.length > DESCRIPTION_LIMIT && "ring-2 ring-red-500/20"
                )}
              ></textarea>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Speaker(s)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className={cn("px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 border", theme.lightBg, theme.text, theme.lightBorder)}>
                  <Shield size={14} />
                  You (Host)
                </div>
                {speakers.map((speaker) => (
                  <div key={speaker.id} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl font-bold text-sm flex items-center gap-2 border border-gray-100">
                    <div className="w-5 h-5 rounded-full bg-brand-indigo/10 flex items-center justify-center overflow-hidden shrink-0">
                      {speaker.avatar ? <img src={speaker.avatar} alt={speaker.name} className="w-full h-full object-cover" /> : <Users size={12} className="text-brand-indigo" />}
                    </div>
                    {speaker.name}
                    <button onClick={() => removeSpeaker(speaker.id)} className="ml-1 hover:text-red-500 transition-colors font-bold text-gray-400">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="relative">
                <div className="relative">
                  <input 
                    type="text" 
                    value={speakerInput}
                    onChange={(e) => setSpeakerInput(e.target.value)}
                    placeholder={community_id ? "Search community members..." : speakerSearchAllowed ? "Search platform members..." : "Only platform admins can assign speakers outside a community"}
                    disabled={!speakerSearchAllowed}
                    className={cn(
                      "w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl font-medium text-sm transition-all focus:ring-2",
                      theme.ring,
                      !speakerSearchAllowed && "cursor-not-allowed opacity-60"
                    )}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                  </div>
                </div>

                <AnimatePresence>
                  {searchResults.length > 0 && speakerInput.trim().length >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto"
                    >
                      {searchResults.map((member) => (
                        <button
                          key={member.id}
                          onClick={() => addSpeaker(member)}
                          className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-indigo/5 flex items-center justify-center overflow-hidden shrink-0">
                            {member.avatar ? <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" /> : <Users size={16} className="text-brand-indigo" />}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900">{member.name}</p>
                            <p className="text-xxs font-medium text-gray-500">{member.email}</p>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <p className="text-xxs font-medium text-gray-400 mt-2 flex items-center gap-1.5 px-1">
                <Info size={10} />
                {community_id
                  ? 'Speakers must be current members of this community.'
                  : speakerSearchAllowed
                    ? 'Outside a community, only platform admins can assign additional speakers.'
                    : 'This board is not linked to a community, so only platform admins may add speakers.'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Board Template</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { id: 'blank', label: 'Blank Canvas', desc: 'Infinite space for free-form ideation', icon: Layout, color: 'text-blue-500', bgColor: 'bg-blue-50' },
                  { id: 'swot', label: 'SWOT Analysis', desc: 'Strategic planning for projects', icon: BrainCircuit, color: 'text-purple-500', bgColor: 'bg-purple-50' },
                  { id: 'design', label: 'Design Thinking', desc: 'User-centric problem solving', icon: Lightbulb, color: 'text-amber-500', bgColor: 'bg-amber-50' },
                  { id: 'ai', label: 'AI Assisted', desc: 'Auto-cluster & categorize community ideas', icon: Sparkles, color: 'text-green-500', bgColor: 'bg-green-50' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTemplate(t.id)}
                    className={cn(
                      "group flex items-start gap-4 p-5 rounded-[2rem] border-2 transition-all text-left",
                      template === t.id ? "border-green-600 bg-green-50 shadow-lg shadow-green-100/50" : "border-gray-50 bg-white hover:border-gray-100"
                    )}
                  >
                    <div className={cn("p-3 rounded-2xl transition-all group-hover:scale-110 shadow-sm", template === t.id ? "bg-green-600 text-white" : cn(t.bgColor, t.color))}>
                      <t.icon size={22} />
                    </div>
                    <div>
                      <h4 className={cn("font-bold text-sm transition-colors", template === t.id ? "text-green-900" : "text-gray-900")}>{t.label}</h4>
                      <p className="text-[11px] text-gray-500 font-medium leading-relaxed mt-1">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button 
            onClick={nextStep}
            className="w-full bg-green-600 text-white font-bold py-4 rounded-2xl shadow-xl shadow-green-200 flex items-center justify-center gap-2 hover:bg-green-700 transition-all text-lg"
          >
            Next: Collaboration
            <ArrowRight size={20} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-5">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                  <Shield size={16} />
                  {error}
                </div>
              )}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Scheduling</h2>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <button 
                    onClick={() => setTimeframe('now')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all",
                      timeframe === 'now' ? "bg-green-600 text-white border-green-600 shadow-lg shadow-green-100" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Radio size={22} className={cn("mb-2", timeframe === 'now' && "animate-pulse")} />
                    <span className="font-bold text-sm">Now</span>
                  </button>
                  <button 
                    onClick={() => setTimeframe('scheduled')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all",
                      timeframe === 'scheduled' ? "bg-green-600 text-white border-green-600 shadow-lg shadow-green-100" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Calendar size={22} className="mb-2" />
                    <span className="font-bold text-sm">Schedule</span>
                  </button>
                </div>

                {timeframe === 'scheduled' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 space-y-3"
                  >
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar size={14} />
                      <label className="text-xxs font-bold uppercase tracking-widest">Date & Time</label>
                    </div>
                    <input 
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-green-500/10 font-bold text-sm transition-all"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </motion.div>
                )}

                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Expected Duration</h2>
                <div className="grid grid-cols-3 gap-2">
                  {['30m', '1h', '2h'].map((d) => (
                    <button 
                      key={d} 
                      onClick={() => setDuration(d)}
                      className={cn(
                        "py-3 rounded-xl font-bold transition-all text-sm",
                        duration === d ? "bg-green-600 text-white shadow-md shadow-green-100" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Submission Window</h2>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'always', label: 'Always Open' },
                    { id: 'session', label: 'During Session' },
                  ].map((p) => (
                    <button 
                      key={p.id}
                      onClick={() => setError('Only "Always Open" is supported in the current version.')}
                      className={cn(
                        "py-3 rounded-xl font-bold transition-all text-sm border-2",
                        p.id === 'always' ? "bg-green-600 text-white border-green-600 shadow-md shadow-green-100" : "bg-white text-gray-400 border-gray-50 cursor-not-allowed"
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Board Permissions</h2>
                <div className="space-y-2">
                  <button 
                    onClick={() => setAnonymous(!anonymous)}
                    className="w-full flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl font-bold text-sm text-gray-600 transition-all"
                  >
                    Anonymous Contributions
                    <div className={cn("w-10 h-6 rounded-full relative transition-colors", anonymous ? "bg-green-500" : "bg-gray-300")}>
                       <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", anonymous ? "right-1" : "left-1")}></div>
                    </div>
                  </button>
                  <button 
                    onClick={() => setVoting(!voting)}
                    className="w-full flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl font-bold text-sm text-gray-600 transition-all"
                  >
                    Voting Enabled
                    <div className={cn("w-10 h-6 rounded-full relative transition-colors", voting ? "bg-green-500" : "bg-gray-300")}>
                       <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", voting ? "right-1" : "left-1")}></div>
                    </div>
                  </button>
                </div>
              </section>
            </div>

            <div className="space-y-5">
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Board Access</h2>
                <div className="space-y-2">
                  <button 
                    onClick={() => setPrivacy('public')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'public' ? "border-green-600 bg-green-50" : "border-gray-50 bg-white"
                    )}
                  >
                    <Globe size={18} className={privacy === 'public' ? "text-green-600" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Public Board</p>
                      <p className="text-xxs text-gray-500 font-medium">Anyone with the link can add ideas</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setPrivacy('private')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'private' ? "border-green-600 bg-green-50" : "border-gray-50 bg-white"
                    )}
                  >
                    <Lock size={18} className={privacy === 'private' ? "text-green-600" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Invite Only</p>
                      <p className="text-xxs text-gray-500 font-medium">Selected collaborators only</p>
                    </div>
                  </button>
                </div>
              </section>

              {privacy === 'private' && (
                <motion.section
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 pt-2"
                >
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Private Access Configuration</h2>
                  <div className="bg-white rounded-[2rem] border border-green-100 p-6 space-y-5 shadow-sm">
                    <button 
                      onClick={() => setInviteOnly(!inviteOnly)}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn("p-2.5 rounded-xl transition-colors", inviteOnly ? "bg-green-100 text-green-600" : "bg-gray-50 text-gray-400")}>
                          <Shield size={18} />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm text-gray-900">Invite Only</p>
                          <p className="text-xxs text-gray-500 font-medium uppercase tracking-wider">Restricted to community members</p>
                        </div>
                      </div>
                      <div className={cn("w-12 h-7 rounded-full relative transition-colors p-1", inviteOnly ? "bg-green-600" : "bg-gray-200")}>
                        <div className={cn("w-5 h-5 bg-white rounded-full transition-all shadow-sm", inviteOnly ? "translate-x-5" : "translate-x-0")}></div>
                      </div>
                    </button>

                    <div className="h-px bg-gray-50 w-full"></div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Key size={14} />
                        <label className="text-xxs font-bold uppercase tracking-widest">Access Password</label>
                      </div>
                      <input 
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Optional: Set a password"
                        className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-green-500/10 font-bold text-sm transition-all"
                      />
                    </div>
                  </div>
                </motion.section>
              )}
            </div>
          </div>

          <button 
            onClick={handleCreate}
            disabled={loading}
            className={cn(
              "w-full bg-green-600 text-white font-bold py-4 rounded-2xl shadow-xl shadow-green-200 flex items-center justify-center gap-2 hover:bg-green-700 transition-all text-lg",
              loading && "opacity-70 cursor-not-allowed"
            )}
          >
            {loading ? (
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              "Launch Ideation Board"
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default BrainstormCreation;
